import { motion, Variants } from "framer-motion";
import styled from "styled-components";
import { ReactComponent as ExploreSvg } from "../../assets/svg/museum.svg";
import { textRotationAnimation } from "../../styles/animations";
import { fill } from "../../styles/utils";

interface SceneMenuButtonProps {
  isOpen: boolean;
  onClick: () => void;
  isAnimating: boolean;
}

const Wrapper = styled.div`
  position: relative;
  height: 110px;
  width: 110px;
  svg {
    position: absolute;
    height: 100%;
    width: 100%;
  }
`;

const PosCorrector = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledButton = styled(motion.button)`
  width: 72px;
  height: 72px;
  background-color: ${({ theme }) => theme.colors.blueDark};
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  /* background-color: transparent; */

  &:disabled {
    cursor: default;
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Text = styled(motion.g)`
  fill: ${({ theme }) => theme.colors.white};
`;

const textVariants: Variants = {
  animating: textRotationAnimation,
  hidden: { scale: 0, transition: { duration: 0.6 } },
};

export const SceneMenuButton: React.FC<SceneMenuButtonProps> = ({
  isOpen,
  onClick,
  isAnimating = false,
}) => {
  return (
    <Wrapper>
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <Text
          fillRule="nonzero"
          animate={isAnimating ? "animating" : "hidden"}
          variants={textVariants}
        >
          <path
            d="M17.0016 20.4288L20.9581 16.9679C21.0684 16.8568 21.1705 16.7648 21.26 16.692C21.3499 16.621 21.4276 16.5568 21.49 16.5019L21.775 16.2527C22.6114 15.5208 23.544 15.0361 24.5708 14.7999C25.5976 14.5638 26.6065 14.5983 27.5971 14.9019C28.5887 15.206 29.4499 15.7773 30.1818 16.6136C30.901 17.4356 31.3644 18.3714 31.5725 19.4225C31.7816 20.4742 31.7141 21.5088 31.3709 22.5268C31.0278 23.5449 30.4293 24.4266 29.577 25.1717C28.7251 25.9184 27.7713 26.3937 26.7166 26.5983C25.6619 26.803 24.6264 26.7321 23.6129 26.386C22.5983 26.0393 21.7319 25.4552 21.0138 24.6338C19.8947 23.3544 19.5572 21.956 20.0007 20.4396L18.3064 21.9216L17.0016 20.4288ZM28.6088 17.97C28.1175 17.4096 27.5435 17.0236 26.8842 16.8121C26.2259 16.6012 25.5547 16.5702 24.8714 16.7222C24.1887 16.8733 23.5709 17.1904 23.0174 17.6745C22.426 18.1928 22.0097 18.7758 21.769 19.4252C21.5293 20.0752 21.4779 20.7333 21.6152 21.4011C21.753 22.0678 22.0701 22.6856 22.5675 23.2551C23.0578 23.8149 23.6458 24.2201 24.3315 24.4706C25.0172 24.7212 25.7162 24.7864 26.4268 24.6669C27.138 24.5464 27.7725 24.2417 28.334 23.7509C28.8939 23.2606 29.2818 22.671 29.4945 21.983C29.7077 21.294 29.7362 20.5933 29.5789 19.8804C29.4232 19.1671 29.0985 18.5309 28.6088 17.97Z"
            fill="white"
          />
          <path
            d="M11.6914 27.3453C12.2735 26.4847 12.9797 25.9419 13.8077 25.7183C14.6374 25.4942 15.5175 25.6962 16.4473 26.3252L20.0186 28.7422L18.9336 30.3454L15.7272 28.1754C15.1883 27.8112 14.7171 27.637 14.3125 27.655C13.9079 27.673 13.5467 27.916 13.2295 28.3856C12.9111 28.8547 12.8205 29.2804 12.9538 29.6621C13.0881 30.0444 13.4248 30.4177 13.9637 30.782L17.1701 32.9519L16.0851 34.5551L12.5139 32.1382C11.5841 31.5091 11.0683 30.7682 10.9682 29.915C10.8681 29.0618 11.1099 28.2047 11.6914 27.3453Z"
            fill="white"
          />
          <path
            d="M13.3056 40.3575L6.47954 37.3097L7.26853 35.5413L14.0946 38.5891L13.3056 40.3575ZM15.0912 40.1024C15.2361 39.7765 15.4686 39.5529 15.7867 39.4304C16.1059 39.3086 16.4273 39.3208 16.7533 39.4657C17.0797 39.6122 17.3001 39.8429 17.4178 40.1596C17.5354 40.4764 17.5211 40.7966 17.3763 41.1226C17.2304 41.4479 17.001 41.6733 16.686 41.7976C16.3727 41.9214 16.0527 41.9114 15.7274 41.7655C15.4014 41.6207 15.1778 41.3882 15.056 41.069C14.9335 40.7508 14.9453 40.4278 15.0912 40.1024Z"
            fill="white"
          />
          <path
            d="M3.35329 46.2545C3.55588 45.4726 3.9014 44.8306 4.38984 44.3284C4.87768 43.8272 5.44859 43.4872 6.10031 43.31C6.75369 43.1322 7.43148 43.1349 8.1316 43.3169C8.83172 43.4989 9.42565 43.8236 9.91067 44.2909C10.3968 44.7589 10.7288 45.3266 10.9066 45.9968C11.086 46.6666 11.077 47.377 10.8822 48.1285C10.6552 49.0011 10.249 49.7143 9.66048 50.2664L7.80204 49.7833L7.80983 49.7528C8.4559 49.227 8.87433 48.5991 9.06364 47.8669C9.23 47.2277 9.184 46.6657 8.92459 46.1805C8.66562 45.6969 8.23685 45.3766 7.63767 45.2208C7.05873 45.0711 6.53138 45.137 6.05773 45.4199C5.58242 45.7033 5.25546 46.1897 5.07565 46.8812C4.87641 47.6525 4.9238 48.4081 5.22068 49.1457L5.21288 49.1761L3.35384 48.6941C3.11341 47.9919 3.11282 47.1794 3.35329 46.2545Z"
            fill="white"
          />
          <path
            d="M1.84744 58.8647L5.89696 56.1145L1.96566 55.9555L2.04484 54.0219L13.4448 54.4837L13.8379 56.4371L5.95971 56.1172L9.32475 58.9784L9.22658 61.4001L5.853 58.3648L1.75024 61.2703L1.84744 58.8647Z"
            fill="white"
          />
          <path
            d="M93.473 107.81C92.5506 108.449 91.5583 108.814 90.4975 108.903C89.4372 108.994 88.4289 108.813 87.4741 108.365C86.5189 107.916 85.724 107.235 85.0917 106.32C84.4583 105.405 84.1018 104.415 84.0199 103.349C83.9386 102.281 84.1403 101.252 84.6274 100.258C85.1133 99.2637 85.8605 98.4175 86.87 97.7198C87.4829 97.2951 88.0838 96.9612 88.6754 96.7185C89.266 96.4752 89.8128 96.3393 90.3162 96.3099L91.498 98.0182L91.4725 98.0357C90.3974 97.9755 89.2517 98.3666 88.0356 99.2092C87.3113 99.7108 86.7825 100.307 86.4498 100.997C86.1161 101.686 85.9847 102.393 86.0524 103.119C86.1206 103.843 86.3693 104.517 86.799 105.138C87.2293 105.758 87.7644 106.231 88.4023 106.554C89.0413 106.878 89.712 107.017 90.4139 106.971C91.1152 106.925 91.7817 106.684 92.4109 106.248C92.9457 105.878 93.3506 105.448 93.6235 104.958C93.8975 104.469 94.0215 103.957 93.9946 103.42C93.9671 102.884 93.7773 102.361 93.4255 101.851L93.4159 101.839L90.402 103.926L89.273 102.296L93.9698 99.0441L94.7315 100.144C95.3169 100.989 95.6836 101.875 95.832 102.799C95.9811 103.722 95.8646 104.624 95.4827 105.501C95.0998 106.377 94.4305 107.147 93.473 107.81Z"
            fill="white"
          />
          <path
            d="M104.102 98.1993C103.422 98.9856 102.659 99.442 101.809 99.5678C100.959 99.6936 100.109 99.3904 99.2589 98.6576L95.9936 95.8414L97.257 94.3748L100.189 96.9035C100.682 97.328 101.129 97.5555 101.534 97.5853C101.938 97.6154 102.325 97.4152 102.695 96.9862C103.065 96.5566 103.205 96.1441 103.117 95.7494C103.028 95.3541 102.738 94.9445 102.245 94.5201L99.3124 91.9913L100.577 90.5253L103.842 93.3414C104.692 94.0742 105.118 94.8704 105.118 95.7301C105.118 96.5887 104.78 97.4123 104.102 98.1993Z"
            fill="white"
          />
          <path
            d="M103.99 85.0998L110.42 88.914L109.432 90.5789L103.002 86.7647L103.99 85.0998ZM102.188 85.1467C102.006 85.4542 101.75 85.6487 101.419 85.733C101.089 85.8179 100.769 85.7688 100.463 85.5867C100.156 85.405 99.9633 85.1499 99.8826 84.8223C99.8026 84.4937 99.8541 84.1769 100.036 83.8694C100.218 83.5636 100.472 83.3662 100.799 83.2795C101.124 83.1922 101.441 83.2394 101.748 83.4222C102.054 83.6033 102.25 83.8602 102.335 84.1901C102.419 84.5212 102.371 84.8399 102.188 85.1467Z"
            fill="white"
          />
          <path
            d="M104.682 72.4933L115.809 76.6573L115.131 78.4704L114.572 78.2606C114.812 79.0229 114.79 79.7874 114.503 80.5539C114.264 81.1931 113.878 81.7297 113.346 82.1631C112.814 82.5971 112.209 82.8764 111.527 83.0031C110.848 83.1293 110.174 83.0672 109.505 82.8182C108.837 82.5683 108.288 82.1714 107.858 81.6298C107.428 81.0876 107.157 80.4796 107.045 79.8058C106.933 79.1304 106.997 78.4736 107.237 77.835C107.522 77.0689 108.008 76.4771 108.69 76.0603L104.445 74.4718L104.682 72.4933ZM112.837 79.796C112.97 79.4422 113.001 79.0873 112.932 78.7317C112.864 78.3751 112.715 78.057 112.484 77.7741C112.255 77.4918 111.966 77.2863 111.623 77.1578C111.279 77.0292 110.93 76.9968 110.576 77.0597C110.221 77.123 109.902 77.2671 109.616 77.4907C109.331 77.715 109.122 78.0024 108.99 78.3567C108.864 78.6909 108.835 79.039 108.903 79.3993C108.968 79.7602 109.118 80.0826 109.348 80.3638C109.578 80.6461 109.86 80.8496 110.194 80.9754C110.538 81.104 110.89 81.1382 111.249 81.0756C111.609 81.0148 111.932 80.8688 112.219 80.6403C112.506 80.4118 112.712 80.1307 112.837 79.796Z"
            fill="white"
          />
          <path
            d="M110.026 66.7011C110.122 65.9725 110.378 65.3581 110.793 64.8569C111.209 64.3564 111.73 63.9998 112.355 63.7852C112.982 63.5722 113.653 63.5127 114.372 63.6074C114.507 63.6252 114.708 63.6617 114.976 63.7185L114.274 69.0387C114.748 68.9744 115.143 68.7649 115.46 68.4097C115.777 68.0545 115.978 67.5497 116.065 66.8943C116.185 65.9795 116.048 65.0981 115.652 64.2526L115.656 64.2214L117.481 64.4616C117.661 64.8564 117.776 65.2733 117.823 65.7134C117.871 66.1534 117.859 66.644 117.788 67.1859C117.669 68.09 117.39 68.8364 116.953 69.4241C116.516 70.0118 115.979 70.4232 115.345 70.6569C114.711 70.8906 114.046 70.9607 113.349 70.8701C112.683 70.7818 112.08 70.5428 111.538 70.1545C110.997 69.7651 110.587 69.267 110.308 68.6586C110.028 68.0495 109.934 67.3974 110.026 66.7011ZM113.322 65.4209C112.894 65.4495 112.546 65.5854 112.276 65.8306C112.006 66.0758 111.84 66.4259 111.779 66.8844C111.724 67.3106 111.792 67.6955 111.984 68.0384C112.178 68.3808 112.474 68.6427 112.873 68.8228L113.322 65.4209Z"
            fill="white"
          />
        </Text>
      </svg>
      <PosCorrector>
        <StyledButton
          onClick={onClick}
          whileHover={!isAnimating ? { scale: 1.1 } : { scale: 1 }}
          whileTap={{ scale: 1 }}
        >
          <ExploreSvg />
        </StyledButton>
      </PosCorrector>
    </Wrapper>
  );
};
